import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  LightBulbIcon,
  ExclamationTriangleIcon,
  ArrowTrendingUpIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  CogIcon,
  ClockIcon,
  FireIcon
} from '@heroicons/react/24/outline';

/**
 * AI-Powered Business Intelligence Hub
 * Provides smart insights, recommendations, and alerts
 */
const BusinessIntelligenceHub = () => {
  const [insights, setInsights] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    generateBusinessIntelligence();
  }, []);

  const generateBusinessIntelligence = async () => {
    setLoading(true);
    
    try {
      // Fetch real business intelligence data from API
      const response = await axios.get('/api/admin/dashboard/business-intelligence');
      const data = response.data;
      
      setInsights(data.insights || []);
      setRecommendations(data.recommendations || []);
      setAlerts(data.alerts || []);
      
    } catch (error) {
      console.error('Error fetching business intelligence:', error);
      // Fall back to mock data if API fails
      
      // Generate AI-powered insights
      setInsights([
      {
        id: 1,
        type: 'opportunity',
        icon: ArrowTrendingUpIcon,
        title: 'זמן להגדיל מלאי',
        description: 'המכירות של מחשבים ניידים עלו ב-45% השבוע. מומלץ להגדיל הזמנות ב-30% לשבוע הבא.',
        confidence: 92,
        impact: 'high',
        category: 'Inventory',
        actionable: true,
        estimatedRevenue: 25000
      },
      {
        id: 2,
        type: 'trend',
        icon: ChartBarIcon,
        title: 'מגמת לקוחות B2B',
        description: 'לקוחות B2B מבצעים קניות גדולות יותר ביום רביעי-חמישי. התמקדו בפרסום B2B באמצע השבוע.',
        confidence: 87,
        impact: 'medium',
        category: 'Marketing',
        actionable: true,
        estimatedRevenue: 15000
      },
      {
        id: 3,
        type: 'risk',
        icon: ExclamationTriangleIcon,
        title: 'ירידה בשביעות רצון',
        description: 'דירוג שביעות הרצון של לקוחות חדשים ירד ב-12%. יש לבדוק את תהליך ההטמעה.',
        confidence: 78,
        impact: 'high',
        category: 'Customer Experience',
        actionable: true,
        estimatedRevenue: -18000
      },
      {
        id: 4,
        type: 'cost-optimization',
        icon: CurrencyDollarIcon,
        title: 'חיסכון בעלויות משלוח',
        description: 'איחוד משלוחים לאזור המרכז יחסוך עד 2,400₪ בחודש. 73% מההזמנות מגיעות מאזור זה.',
        confidence: 95,
        impact: 'medium',
        category: 'Operations',
        actionable: true,
        estimatedRevenue: 2400
      },
      {
        id: 5,
        type: 'seasonal',
        icon: ClockIcon,
        title: 'הכנה לעונה',
        description: 'בהתבסס על נתוני שנים קודמות, מכירות חורף יתחילו לעלות בעוד 3 שבועות. הזמן להכין מלאי.',
        confidence: 89,
        impact: 'high',
        category: 'Planning',
        actionable: true,
        estimatedRevenue: 45000
      }
    ]);

    setRecommendations([
      {
        id: 1,
        title: 'הוסף קטגוריית אביזרים',
        description: 'לקוחות מחפשים אביזרים נלווים. הוסף קטגוריית אביזרי מחשב.',
        priority: 'high',
        effort: 'low',
        roi: 340,
        timeframe: '2 שבועות'
      },
      {
        id: 2,
        title: 'שפר תהליך קופה',
        description: '23% מהלקוחות נוטשים בשלב התשלום. קצר את התהליך.',
        priority: 'high',
        effort: 'medium',
        roi: 280,
        timeframe: '1 חודש'
      },
      {
        id: 3,
        title: 'תכנית נאמנות B2B',
        description: 'לקוחות B2B חוזרים פחות מB2C. צור תכנית נאמנות ייעודית.',
        priority: 'medium',
        effort: 'high',
        roi: 180,
        timeframe: '6 שבועות'
      }
    ]);

    setAlerts([
      {
        id: 1,
        type: 'critical',
        title: 'מלאי אוזל',
        message: '3 מוצרים יגמרו תוך 2 ימים',
        timestamp: new Date()
      },
      {
        id: 2,
        type: 'warning', 
        title: 'הזמנה חריגה',
        message: 'הזמנה של 50,000₪ מחכה לאישור',
        timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000)
      }
      ]);
    }

    setLoading(false);
  };

  const getInsightColor = (type) => {
    switch (type) {
      case 'opportunity': return 'bg-green-50 border-green-200';
      case 'trend': return 'bg-blue-50 border-blue-200';
      case 'risk': return 'bg-red-50 border-red-200';
      case 'cost-optimization': return 'bg-purple-50 border-purple-200';
      case 'seasonal': return 'bg-orange-50 border-orange-200';
      default: return 'bg-gray-50 border-gray-200';
    }
  };

  const getInsightIconColor = (type) => {
    switch (type) {
      case 'opportunity': return 'text-green-600';
      case 'trend': return 'text-blue-600';
      case 'risk': return 'text-red-600';
      case 'cost-optimization': return 'text-purple-600';
      case 'seasonal': return 'text-orange-600';
      default: return 'text-gray-600';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="animate-pulse">
            <div className="flex items-center mb-4">
              <div className="w-8 h-8 bg-gray-200 rounded-full mr-3"></div>
              <div className="h-4 bg-gray-200 rounded w-48"></div>
            </div>
            <div className="space-y-3">
              <div className="h-4 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded w-5/6"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <div className="p-2 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg mr-3">
            <LightBulbIcon className="h-6 w-6 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">מרכז מודיעין עסקי</h3>
            <p className="text-sm text-gray-600">תובנות והמלצות מבוססות AI</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <div className="flex items-center bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs">
            <div className="w-2 h-2 bg-green-500 rounded-full mr-1 animate-pulse"></div>
            פעיל
          </div>
        </div>
      </div>

      {/* Critical Alerts */}
      {alerts.length > 0 && (
        <div className="bg-red-50 border-r-4 border-red-400 p-4 rounded-lg">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400 ml-2" />
            <div>
              <h4 className="text-sm font-medium text-red-800">התראות קריטיות</h4>
              <div className="mt-2 space-y-1">
                {alerts.map((alert) => (
                  <p key={alert.id} className="text-sm text-red-700">
                    {alert.message} • <span className="text-red-600">{alert.title}</span>
                  </p>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* AI Insights */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          <h4 className="text-md font-semibold text-gray-900 flex items-center">
            <FireIcon className="h-5 w-5 text-orange-500 ml-2" />
            תובנות חמות
          </h4>
          
          {insights.map((insight) => (
            <div key={insight.id} className={`p-4 rounded-lg border ${getInsightColor(insight.type)}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-start">
                  <insight.icon className={`h-6 w-6 ml-3 mt-0.5 ${getInsightIconColor(insight.type)}`} />
                  <div className="flex-1">
                    <h5 className="font-semibold text-gray-900 mb-1">{insight.title}</h5>
                    <p className="text-sm text-gray-700 mb-3">{insight.description}</p>
                    
                    <div className="flex items-center space-x-4 space-x-reverse">
                      <div className="flex items-center">
                        <span className="text-xs text-gray-600">ביטחון:</span>
                        <div className="w-20 bg-gray-200 rounded-full h-2 mr-2">
                          <div 
                            className="bg-blue-600 h-2 rounded-full" 
                            style={{ width: `${insight.confidence}%` }}
                          ></div>
                        </div>
                        <span className="text-xs font-medium text-gray-700">{insight.confidence}%</span>
                      </div>
                      
                      {insight.estimatedRevenue && (
                        <div className="flex items-center text-xs">
                          <CurrencyDollarIcon className="h-4 w-4 text-green-600 ml-1" />
                          <span className={`font-medium ${insight.estimatedRevenue > 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {insight.estimatedRevenue > 0 ? '+' : ''}{insight.estimatedRevenue.toLocaleString('he-IL')}₪
                          </span>
                        </div>
                      )}
                    </div>
                    
                    {insight.actionable && (
                      <button className="mt-3 text-xs bg-blue-600 text-white px-3 py-1 rounded-full hover:bg-blue-700 transition-colors">
                        בצע פעולה
                      </button>
                    )}
                  </div>
                </div>
                
                <div className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                  {insight.category}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Strategic Recommendations */}
        <div className="space-y-4">
          <h4 className="text-md font-semibold text-gray-900 flex items-center">
            <CogIcon className="h-5 w-5 text-blue-500 ml-2" />
            המלצות אסטרטגיות
          </h4>
          
          {recommendations.map((rec) => (
            <div key={rec.id} className="bg-white p-4 rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <h5 className="font-semibold text-gray-900">{rec.title}</h5>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(rec.priority)}`}>
                      {rec.priority === 'high' ? 'גבוה' : rec.priority === 'medium' ? 'בינוני' : 'נמוך'}
                    </span>
                  </div>
                  
                  <p className="text-sm text-gray-700 mb-3">{rec.description}</p>
                  
                  <div className="grid grid-cols-3 gap-4 text-xs">
                    <div>
                      <span className="text-gray-600">ROI צפוי:</span>
                      <div className="font-semibold text-green-600">{rec.roi}%</div>
                    </div>
                    <div>
                      <span className="text-gray-600">מאמץ:</span>
                      <div className="font-semibold text-blue-600">
                        {rec.effort === 'low' ? 'נמוך' : rec.effort === 'medium' ? 'בינוני' : 'גבוה'}
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-600">זמן ביצוע:</span>
                      <div className="font-semibold text-purple-600">{rec.timeframe}</div>
                    </div>
                  </div>
                  
                  <button className="mt-3 text-xs bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-full hover:from-blue-700 hover:to-purple-700 transition-all">
                    התחל ביצוע
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BusinessIntelligenceHub;
